import queryString from "query-string";
import { ASPECT_RATIO, LOCAL_STORAGE_ITEM_NAMES, Q_STRING, SORT_FIELD, VALID_PAGE_SIZES } from "../components/domain/constants";
import Util from "./Util";
import { retrieveUserPreferenceFromLocalStorage } from "./StorageUtil";
import { isNotABlankString, stringToList } from "./StringUtil";
export function retrieveFromQueryString(param, querystringValues) {
  let val = querystringValues[param];
  return val ? val : '';
}

/**
 * Pushes a new URL onto the history stack with the updated queryString parameter for a single param
 *
 * This ensures that navigating backwards or sending links to someone else will preserve the state of the page
 */

export function changeSearchValue(queryParam, value, props) {
  if (props != null && props.location != null) {
    const querystringValues = queryString.parse(props.location.search);
    if (querystringValues != null) {
      const queryStringValue = retrieveFromQueryString(queryParam, querystringValues);
      if (queryStringValue !== value) {
        let currPath = props.location.pathname;
        querystringValues[queryParam] = value;
        props.history.push({
          pathname: currPath,
          search: "?".concat(queryString.stringify(querystringValues))
        });
      }
    }
  } else {
    Util.log('Util.changeSearchValue', true, 'Could not change search value. Is {withRouter} imported from \'react-router-dom\'?', false);
  }
}

/**
 * Pushes a new URL onto the history stack with the updated queryString parameters for multiple params
 */
export function changeSearchValues(paramToValueObj, props) {
  if (props != null && props.location != null) {
    const querystringValues = queryString.parse(props.location.search);
    if (querystringValues != null) {
      const params = Object.keys(paramToValueObj);
      let currentValue, pendingValue;
      for (let param of params) {
        currentValue = querystringValues[param];
        pendingValue = paramToValueObj[param];
        if (currentValue !== pendingValue && pendingValue != null) {
          querystringValues[param] = pendingValue;
        }
      }
      let currPath = props.location.pathname;
      props.history.push({
        pathname: currPath,
        search: "?".concat(queryString.stringify(querystringValues))
      });
    }
  }
}
export function determinePageFromQueryString(props) {
  if (props != null && props.location != null) {
    const querystringValues = queryString.parse(props.location.search);
    const page = retrieveFromQueryString(Q_STRING.PAGE, querystringValues);
    try {
      if (page != null && page !== '') {
        const pageInt = parseInt(page, 10);
        return pageInt - 1;
      }
    } catch (e) {
      Util.log('QueryStringUtil.determinePageFromQueryString', true, e.message, false);
    }
  }
  return 0;
}
export function determinePageSize(queryStringValues, props) {
  const pageFromQString = retrieveFromQueryString(Q_STRING.SIZE, queryStringValues);
  const pageFromLocalStorage = retrieveUserPreferenceFromLocalStorage(LOCAL_STORAGE_ITEM_NAMES.preferredPageSize);
  if (isNotABlankString(pageFromQString)) {
    const parsed = parseAndValidatePageSize(pageFromQString);
    if (parsed !== -1) {
      return parsed;
    }
  } else if (isNotABlankString(pageFromLocalStorage)) {
    const parsed = parseAndValidatePageSize(pageFromLocalStorage);
    if (parsed !== -1) {
      return parsed;
    }
  }
  return props.pageSize != null ? props.pageSize : 10;
}
export function retrieveAndDecodeTitle(querystringValues) {
  const encoded = retrieveFromQueryString(Q_STRING.TITLE, querystringValues);
  let decoded = encoded;
  try {
    decoded = decodeURIComponent(encoded);
  } catch (err) {
    if (err.name === "URIError") {
      console.warn("".concat(encoded, " is either malformed or is already decoded"));
    }
  }
  return decoded;
}
export function determineRatio(querystringValues, props) {
  const ratioFromQString = retrieveFromQueryString(Q_STRING.RATIO, querystringValues);
  const ratioFromLocalStorage = retrieveUserPreferenceFromLocalStorage(LOCAL_STORAGE_ITEM_NAMES.preferredAspectRatio);
  const defaultRatioFromLocalStorage = retrieveUserPreferenceFromLocalStorage(LOCAL_STORAGE_ITEM_NAMES.defaultAspectRatio);
  if (isNotABlankString(ratioFromQString)) {
    return ratioFromQString;
  } else if (isNotABlankString(ratioFromLocalStorage)) {
    return ratioFromLocalStorage;
  } else if (isNotABlankString(defaultRatioFromLocalStorage)) {
    return defaultRatioFromLocalStorage;
  }
  return props.ratio != null ? props.ratio : ASPECT_RATIO.THREE_BY_FOUR;
}
export function determineSelectedFilters(querystringValues, props) {
  const filtersFromQString = retrieveFromQueryString(Q_STRING.FILTERS, querystringValues);
  if (isNotABlankString(filtersFromQString)) {
    const decodedFilters = decodeURIComponent(filtersFromQString);
    if (decodedFilters != null) {
      return urlStringToList(decodedFilters);
    }
  }
  return [];
}
export function listToUrlString(list) {
  let str = "";
  let firstSet = false;
  if (list != null) {
    for (let val of list) {
      if (firstSet === true) {
        str += "+";
      }
      str += val;
      firstSet = true;
    }
  }
  return str;
}
export function urlStringToList(str) {
  if (str != null) {
    return decodeURIComponent(str).split("+");
  }
  return null;
}
function parseAndValidatePageSize(stringPageSize) {
  try {
    const intPageSize = parseInt(stringPageSize);
    if (intPageSize != null && VALID_PAGE_SIZES.includes(intPageSize)) {
      return intPageSize;
    }
  } catch (e) {
    Util.log('QueryStringUtil.parseAndValidatePageSize', true, e.message, false);
  }
  return -1;
}
export function determineSortInfoFromQueryString(querystringValues, props) {
  let field,
    desc = null;
  try {
    field = retrieveFromQueryString(Q_STRING.SORT_FIELD, querystringValues);
    desc = retrieveFromQueryString(Q_STRING.SORT_DESCENDING, querystringValues);
    if (!Object.values(SORT_FIELD).includes(field)) {
      field = props.sortField != null ? props.sortField : SORT_FIELD.title;
    }
    if (typeof desc === 'string' && !(desc === 'true' || desc === 'false')) {
      desc = props.sortDescending != null ? props.sortDescending : false;
    }
  } catch (e) {
    Util.log('Homepage.determineSortInfoFromQueryString', true, e.message, false);
  }
  return {
    field,
    desc
  };
}

/*
    The following functions all attempt to call setState for the particular value they're retrieving from the querystring
    We must check that this isn't just equal to the previous state before we update. Otherwise, the update will cause
    the componentDidUpdate method to be called, and that will trigger the function again, creating a cascading loop
    of updates that essentially kills the webpage.
 */

export function setUpPageInfoFromQueryString(instance) {
  // if page is different in the URL, navigate to it instead of default
  const page = determinePageFromQueryString(instance.props);

  // The state is set automatically when the buttons are used, but if the page is reloaded with the page in the url search params
  // it will need to be handled here.
  if (page != null && page !== '') {
    if (page !== instance.state.page) {
      if (page >= 0 && page < instance.state.totalPages) {
        instance.state.page = page;
      } else {
        instance.state.page = 0;
        changeSearchValue(Q_STRING.PAGE, instance.state.page + 1, instance.props);
      }
      instance.loadContent();
    }
  }
}
export function setUpTabInfoFromQueryString(instance, prevState, defaultValue, querystringValues, tabValueFromQString) {
  if (prevState.tabValue !== defaultValue) {
    if (tabValueFromQString === -1) {
      instance.setState({
        tabValue: defaultValue
      });
    } else if (instance.state.tabValue !== tabValueFromQString) {
      instance.setState({
        tabValue: tabValueFromQString
      });
    }
  }
}
export function setUpSortInfoFromQueryString(instance, prevState, defaultSortField, defaultSortDescending, sortInfo) {
  if (prevState.sortField !== defaultSortField) {
    if (instance.state.sortField !== sortInfo.field) {
      instance.setState({
        sortField: sortInfo.field
      });
    }
  }
  let booleanSortDescending = false;
  if (sortInfo.desc != null && typeof sortInfo.desc === 'string') {
    booleanSortDescending = sortInfo.desc === 'true';
  }
  if (prevState.sortDescending !== defaultSortDescending) {
    if (instance.state.sortDescending !== booleanSortDescending) {
      instance.setState({
        sortDescending: booleanSortDescending
      });
    }
  }
}
export function setUpRatioFromQueryString(instance, prevState, defaultRatio, queryStringValues, ratioValueFromQString) {
  if (ratioValueFromQString != null && ratioValueFromQString !== "" && prevState.ratio !== defaultRatio && prevState.ratio !== ratioValueFromQString) {
    if (ratioValueFromQString !== null) {
      instance.setState({
        ratio: ratioValueFromQString
      });
    }
  }
}
export function setUpFiltersFromQueryString(instance, prevState, querystringValues, filtersFromQueryString) {
  if (filtersFromQueryString != null && filtersFromQueryString !== "" && prevState.selectedFilters !== []) {
    const queryStringFiltersList = urlStringToList(filtersFromQueryString);

    // If the lists aren't the same size, they obviously aren't equal
    if (queryStringFiltersList != null && queryStringFiltersList.size === instance.state.selectedFilters.length) {
      // clone and sort for comparison purposes
      const prevStateFiltersClone = [...prevState.selectedFilters];
      const querystringFiltersClone = [...queryStringFiltersList];
      prevStateFiltersClone.sort();
      querystringFiltersClone.sort();

      // clone wars
      for (let i = 0; i < prevStateFiltersClone.length; ++i) {
        if (prevStateFiltersClone[i] !== querystringFiltersClone[i]) {
          instance.setState({
            selectedFilters: queryStringFiltersList
          });
        }
      }
    }
  }
}